import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { api } from '../lib/api';
import { wsClient } from '../lib/websocket';
import { useAuth } from './auth';

interface ConversationSummary {
  id: string;
  type: string;
  careHomeId?: string;
  familyId?: string;
  dependentId?: string;
  title?: string;
  createdAt: string;
  updatedAt: string;
  dependent?: { id: string; name: string };
  careHome?: { id: string; name: string };
  otherParticipant?: { id: string; name: string };
  lastMessage?: {
    id: string;
    content: string;
    messageType: string;
    senderId: string;
    senderName: string;
    createdAt: string;
  };
  unreadCount: number;
}

interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderRole: 'staff' | 'family' | 'system';
  content: string;
  messageType: string;
  metadata?: Record<string, any>;
  createdAt: string;
}

interface MessagingContextType {
  conversations: ConversationSummary[];
  totalUnreadCount: number;
  activeConversationId: string | null;
  messages: ChatMessage[];
  messagesLoading: boolean;
  hasMoreMessages: boolean;
  sendMessage: (content: string, messageType?: string) => Promise<void>;
  markAsRead: () => Promise<void>;
  loadMoreMessages: () => Promise<void>;
  setActiveConversation: (id: string | null) => void;
  loadConversations: (filter: { careHomeId?: string; familyId?: string }) => Promise<void>;
  openConversation: (filter: { careHomeId?: string; familyId?: string; targetUserId?: string }, dependentId?: string) => Promise<string>;
  conversationsLoading: boolean;
}

const MessagingContext = createContext<MessagingContextType | undefined>(undefined);

export function MessagingProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [messagesCursor, setMessagesCursor] = useState<string | undefined>();
  const activeConvoRef = useRef<string | null>(null);

  // Keep ref in sync
  useEffect(() => {
    activeConvoRef.current = activeConversationId;
  }, [activeConversationId]);

  // Connect WebSocket when user is available
  useEffect(() => {
    if (!user?.id) return;

    wsClient.connect(user.id);

    const unsubNew = wsClient.on('message:new', (data: any) => {
      const msg = data.message as ChatMessage;

      // Update conversation list
      setConversations((prev) => {
        const updated = prev.map((c) => {
          if (c.id === data.conversationId) {
            return {
              ...c,
              lastMessage: {
                id: msg.id,
                content: msg.content,
                messageType: msg.messageType,
                senderId: msg.senderId,
                senderName: msg.senderName,
                createdAt: msg.createdAt,
              },
              updatedAt: msg.createdAt,
              unreadCount:
                activeConvoRef.current === data.conversationId
                  ? c.unreadCount
                  : c.unreadCount + 1,
            };
          }
          return c;
        });
        // Sort by updatedAt
        return updated.sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      });

      // Add to active conversation messages
      if (activeConvoRef.current === data.conversationId) {
        setMessages((prev) => {
          // Avoid duplicates
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
      }
    });

    const unsubRead = wsClient.on('message:read', () => {
      // Could update read receipts UI in the future
    });

    return () => {
      unsubNew();
      unsubRead();
      wsClient.disconnect();
    };
  }, [user?.id]);

  const loadConversations = useCallback(async (filter: { careHomeId?: string; familyId?: string }) => {
    setConversationsLoading(true);
    try {
      const result = await api.getConversations(filter);
      setConversations(result.conversations);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (conversationId: string) => {
    setMessagesLoading(true);
    setMessages([]);
    setMessagesCursor(undefined);
    try {
      const result = await api.getMessages(conversationId);
      // API returns newest first, reverse for display (oldest at top)
      const msgs = (result.messages as ChatMessage[]).reverse();
      setMessages(msgs);
      setHasMoreMessages(result.hasMore);
      setMessagesCursor(result.nextCursor);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const setActiveConversation = useCallback(
    (id: string | null) => {
      setActiveConversationId(id);
      if (id) {
        loadMessages(id);
      } else {
        setMessages([]);
      }
    },
    [loadMessages]
  );

  const loadMoreMessages = useCallback(async () => {
    if (!activeConversationId || !messagesCursor || messagesLoading) return;
    setMessagesLoading(true);
    try {
      const result = await api.getMessages(activeConversationId, messagesCursor);
      const older = (result.messages as ChatMessage[]).reverse();
      setMessages((prev) => [...older, ...prev]);
      setHasMoreMessages(result.hasMore);
      setMessagesCursor(result.nextCursor);
    } catch (err) {
      console.error('Failed to load more messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  }, [activeConversationId, messagesCursor, messagesLoading]);

  const sendMessage = useCallback(
    async (content: string, messageType?: string) => {
      if (!activeConversationId) return;
      const msg = await api.sendMessage(activeConversationId, content, messageType);
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });

      // Update conversation list
      setConversations((prev) => {
        const updated = prev.map((c) => {
          if (c.id === activeConversationId) {
            return {
              ...c,
              lastMessage: {
                id: msg.id,
                content: msg.content,
                messageType: msg.messageType,
                senderId: msg.senderId,
                senderName: msg.senderName,
                createdAt: msg.createdAt,
              },
              updatedAt: msg.createdAt,
            };
          }
          return c;
        });
        return updated.sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      });
    },
    [activeConversationId]
  );

  const markAsRead = useCallback(async () => {
    if (!activeConversationId || messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    try {
      await api.markConversationRead(activeConversationId, lastMsg.id);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === activeConversationId ? { ...c, unreadCount: 0 } : c
        )
      );
    } catch {
      // ignore
    }
  }, [activeConversationId, messages]);

  const openConversation = useCallback(
    async (filter: { careHomeId?: string; familyId?: string; targetUserId?: string }, dependentId?: string) => {
      const convo = await api.getOrCreateConversation(filter, dependentId);
      setActiveConversation(convo.id);
      return convo.id as string;
    },
    [setActiveConversation]
  );

  const totalUnreadCount = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return (
    <MessagingContext.Provider
      value={{
        conversations,
        totalUnreadCount,
        activeConversationId,
        messages,
        messagesLoading,
        hasMoreMessages,
        sendMessage,
        markAsRead,
        loadMoreMessages,
        setActiveConversation,
        loadConversations,
        openConversation,
        conversationsLoading,
      }}
    >
      {children}
    </MessagingContext.Provider>
  );
}

export function useMessaging() {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
}
